import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Alert,
} from 'reactstrap';

import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';
import IntlMessages from '../../../../../../helpers/IntlMessages';
import { Colxx, Separator } from '../../../../../../components/common/CustomBootstrap';

// VALIDATION
const GA4_ID_REGEX = /^G-[A-Z0-9]{6,15}$/i;

// DEFAULT CONFIG
const DEFAULT_GA4_CONFIG = {
  enabled: false,
  measurementId: '',
  events: {
    avatarClick: {
      enabled: true,
      tag: 'chat_avatar_click',
    },
    menuClick: {
      enabled: true,
      tagPrefix: 'chat_menu',
    },
    nudgesClick: {
      enabled: true,
      tag: 'chat_nudge_click',
    },
    chatStarted: {
      enabled: true,
      tag: 'chat_started',
    },
  },
};

const HelpCenterGA4Tracking = ({ botSettings, saveIntegration }) => {
  const ga4Settings = botSettings?.gA4Tracking || DEFAULT_GA4_CONFIG;

  // MAIN SETTINGS
  const [enabled, setEnabled] = useState(ga4Settings.enabled || false);
  const [measurementId, setMeasurementId] = useState(
    ga4Settings.measurementId || ''
  );

  // VALIDATION STATES
  const [idError, setIdError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // EVENT STATES
  const [avatarEnabled, setAvatarEnabled] = useState(
    ga4Settings.events?.avatarClick?.enabled ?? true
  );
  const [avatarTag, setAvatarTag] = useState(
    ga4Settings.events?.avatarClick?.tag || 'chat_avatar_click'
  );

  const [menuEnabled, setMenuEnabled] = useState(
    ga4Settings.events?.menuClick?.enabled ?? true
  );
  const [menuTag, setMenuTag] = useState(
    ga4Settings.events?.menuClick?.tagPrefix || 'chat_menu'
  );

  const [nudgesEnabled, setNudgesEnabled] = useState(
    ga4Settings.events?.nudgesClick?.enabled ?? true
  );
  const [nudgesTag, setNudgesTag] = useState(
    ga4Settings.events?.nudgesClick?.tag || 'chat_nudge_click'
  );

  const [chatStartedEnabled, setChatStartedEnabled] = useState(
    ga4Settings.events?.chatStarted?.enabled ?? true
  );
  const [chatStartedTag, setChatStartedTag] = useState(
    ga4Settings.events?.chatStarted?.tag || 'chat_started'
  );

  const childToggleSnapshotRef = useRef({
    avatarEnabled: ga4Settings.events?.avatarClick?.enabled ?? true,
    menuEnabled: ga4Settings.events?.menuClick?.enabled ?? true,
    nudgesEnabled: ga4Settings.events?.nudgesClick?.enabled ?? true,
    chatStartedEnabled: ga4Settings.events?.chatStarted?.enabled ?? true,
  });

  // SYNC SETTINGS
  useEffect(() => {
    const settings = botSettings?.gA4Tracking || DEFAULT_GA4_CONFIG;

    setEnabled(settings.enabled || false);
    setMeasurementId(settings.measurementId || '');
    setAvatarEnabled(settings.events?.avatarClick?.enabled ?? true);
    setAvatarTag(settings.events?.avatarClick?.tag || 'chat_avatar_click');
    setMenuEnabled(settings.events?.menuClick?.enabled ?? true);
    setMenuTag(settings.events?.menuClick?.tagPrefix || 'chat_menu');
    setNudgesEnabled(settings.events?.nudgesClick?.enabled ?? true);
    setNudgesTag(settings.events?.nudgesClick?.tag || 'chat_nudge_click');
    setChatStartedEnabled(settings.events?.chatStarted?.enabled ?? true);
    setChatStartedTag(settings.events?.chatStarted?.tag || 'chat_started');
    childToggleSnapshotRef.current = {
      avatarEnabled: settings.events?.avatarClick?.enabled ?? true,
      menuEnabled: settings.events?.menuClick?.enabled ?? true,
      nudgesEnabled: settings.events?.nudgesClick?.enabled ?? true,
      chatStartedEnabled: settings.events?.chatStarted?.enabled ?? true,
    };

    // Reset validation states
    setIdError('');
    setGeneralError('');
    setSuccessMsg('');
    setIdTouched(false);
  }, [botSettings]);

  // VALIDATE GA4 ID
  const validateGA4Id = useCallback((id) => {
    if (!id || !id.trim()) return false;
    return GA4_ID_REGEX.test(id.trim());
  }, []);

  // SANITIZE TAGS
  const sanitizeTag = useCallback((value) => {
    return value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }, []);

  // REAL TIME VALIDATION - only when enabled and touched
  useEffect(() => {
    if (!idTouched) {
      setIdError('');
      return;
    }

    if (!measurementId.trim()) {
      setIdError('GA4 Measurement ID is required when tracking is enabled.');
      return;
    }

    if (!validateGA4Id(measurementId)) {
      setIdError('Invalid GA4 ID format. Example: G-ABCD123456');
      return;
    }

    setIdError('');
  }, [measurementId, idTouched, validateGA4Id]);

  const handleGa4Toggle = useCallback(
    (checked) => {
      setEnabled(checked);

      if (!checked) {
        childToggleSnapshotRef.current = {
          avatarEnabled,
          menuEnabled,
          nudgesEnabled,
          chatStartedEnabled,
        };
        setAvatarEnabled(false);
        setMenuEnabled(false);
        setNudgesEnabled(false);
        setChatStartedEnabled(false);
        setIdError('');
        setIdTouched(false);
        setGeneralError('');
        return;
      }

      const snapshot = childToggleSnapshotRef.current;
      setAvatarEnabled(snapshot?.avatarEnabled ?? false);
      setMenuEnabled(snapshot?.menuEnabled ?? false);
      setNudgesEnabled(snapshot?.nudgesEnabled ?? false);
      setChatStartedEnabled(snapshot?.chatStartedEnabled ?? false);
    },
    [avatarEnabled, menuEnabled, nudgesEnabled, chatStartedEnabled]
  );

  // SAVE
  const handleSave = () => {
    setIdTouched(true);
    setGeneralError('');
    setSuccessMsg('');

    // VALIDATION: If enabled, must have valid ID
    if (enabled) {
      if (!measurementId.trim()) {
        setIdError('GA4 Measurement ID is required when tracking is enabled.');
        return;
      }

      if (!validateGA4Id(measurementId)) {
        setIdError('Invalid GA4 ID format. Example: G-ABCD123456');
        return;
      }

      // At least one event must be enabled
      if (
        !avatarEnabled &&
        !menuEnabled &&
        !nudgesEnabled &&
        !chatStartedEnabled
      ) {
        setGeneralError(
          'Please enable at least one tracking event when GA4 is active.'
        );
        return;
      }
    }

    // Validate tag names for enabled events
    if (enabled) {
      if (avatarEnabled && !sanitizeTag(avatarTag)) {
        setGeneralError('Avatar Click tag is required.');
        return;
      }

      if (menuEnabled && !sanitizeTag(menuTag)) {
        setGeneralError('Menu Click prefix is required.');
        return;
      }

      if (nudgesEnabled && !sanitizeTag(nudgesTag)) {
        setGeneralError('Nudge Click tag is required.');
        return;
      }

      if (chatStartedEnabled && !sanitizeTag(chatStartedTag)) {
        setGeneralError('Chat Started tag is required.');
        return;
      }
    }

    try {
      setSaving(true);
      childToggleSnapshotRef.current = {
        avatarEnabled,
        menuEnabled,
        nudgesEnabled,
        chatStartedEnabled,
      };
      const payload = {
        botSetting: {
          ...botSettings,
          gA4Tracking: {
            enabled,
            measurementId: measurementId.trim(),
            events: {
              avatarClick: {
                enabled: avatarEnabled,
                tag: sanitizeTag(avatarTag) || 'chat_avatar_click',
              },
              menuClick: {
                enabled: menuEnabled,
                tagPrefix: sanitizeTag(menuTag) || 'chat_menu',
              },
              nudgesClick: {
                enabled: nudgesEnabled,
                tag: sanitizeTag(nudgesTag) || 'chat_nudge_click',
              },
              chatStarted: {
                enabled: chatStartedEnabled,
                tag: sanitizeTag(chatStartedTag) || 'chat_started',
              },
            },
          },
        },
      };

      saveIntegration(payload);

      setSuccessMsg('GA4 settings saved successfully.');

      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (error) {
      setGeneralError('Something went wrong while saving settings.');
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled = saving || (enabled && !!idError);
  const sectionDisabled = !enabled;

  return (
    <Card className="mb-4">
      <CardBody>
        <div className="text-center">
          <h6><IntlMessages id="integration.google_analytics" /></h6>
          <p><IntlMessages id="integration.google_analytics_desc" /></p>
        </div>
        <Separator className="mb-3" />
        <Card className="m-4 p-4">
          {/* ENABLE TOGGLE */}
          <FormGroup className="mb-3">
            <Colxx md="12" className="px-0 d-flex align-items-center">
              <h6 className="mb-0"><IntlMessages id="integration.enable_ga4" /></h6>
              <Switch
                className="agentswitch custom-switch-primary custom-switch-small ml-2"
                checked={enabled}
                onChange={handleGa4Toggle}
              />
            </Colxx>
          </FormGroup>
          <Card className="p-4 mb-4 bg-light">
            {/* GA4 ID */}
            <FormGroup className="mb-0">
              <Label for="ga4-id"><b><IntlMessages id="integration.ga4_measurement_id" /> :</b></Label>
              <Input
                id="ga4-id"
                type="text"
                placeholder="G-XXXXXXXXXX"
                value={measurementId}
                onChange={(e) => {
                  setMeasurementId(e.target.value.toUpperCase());
                  setIdTouched(true);
                }}
                invalid={idTouched && !!idError}
                valid={idTouched && !idError && measurementId.length > 0}
              />
              {enabled && idTouched && idError && (
                <FormFeedback style={{ display: 'block' }}>{idError}</FormFeedback>
              )}
              <p className="form-text text-muted text-small mt-1">
                <IntlMessages id="integration.ga4_id_example" />
              </p>
            </FormGroup>
          </Card>
          <Card className="p-4 bg-light">
            {/* TRACK EVENTS */}
            <div className="mb-3">
              <h6><IntlMessages id="integration.track_events" /></h6>
              <p className="text-muted">
                <IntlMessages id="integration.track_events_desc" />
              </p>
            </div>
            <div
              style={{
                opacity: sectionDisabled ? 0.5 : 1,
                pointerEvents: sectionDisabled ? 'none' : 'auto',
              }}
            >
              {/* AVATAR CLICK */}
              <Card className="mb-4">
                <CardBody className="my-2">
                  <div className="mb-3 d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1"><IntlMessages id="integration.avatar_click" /></h6>
                      <p className="text-muted text-small mb-0"><IntlMessages id="integration.avatar_click_desc" /></p>
                    </div>
                    <Switch
                      checked={avatarEnabled}
                      onChange={setAvatarEnabled}
                      disabled={sectionDisabled}
                    />
                  </div>
                  <div>
                    <Label className="mb-1"><IntlMessages id="integration.event_tag" /></Label>
                    <Input
                      type="text"
                      value={avatarTag}
                      onChange={(e) => setAvatarTag(e.target.value)}
                      disabled={!avatarEnabled || sectionDisabled}
                    />
                    <p className="text-muted text-small my-1">
                      <IntlMessages id="integration.final_tag" />:{' '}
                      <code>{sanitizeTag(avatarTag)}</code>
                    </p>
                  </div>
                </CardBody>
              </Card>

              {/* MENU CLICK */}
              <Card className="mb-4">
                <CardBody className="my-2">
                  <div className="mb-3 d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">
                        <IntlMessages id="integration.menu_click" />
                      </h6>
                      <p className="text-muted text-small mb-0">
                        <IntlMessages id="integration.menu_click_desc" />
                      </p>
                    </div>
                    <Switch
                      checked={menuEnabled}
                      onChange={setMenuEnabled}
                      disabled={sectionDisabled}
                    />
                  </div>
                  <div>
                    <Label className="mb-1">
                      <IntlMessages id="integration.event_prefix" />
                    </Label>
                    <Input
                      type="text"
                      value={menuTag}
                      onChange={(e) => setMenuTag(e.target.value)}
                      disabled={!menuEnabled || sectionDisabled}
                    />
                    <p className="text-muted text-small my-1">
                      <IntlMessages id="integration.example_event" />:{' '}
                      <code>{sanitizeTag(menuTag)}_start_chat</code>
                    </p>
                  </div>
                </CardBody>
              </Card>

              {/* NUDGE CLICK */}
              <Card className="mb-4">
                <CardBody className="my-2">
                  <div className="mb-3 d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">
                        <IntlMessages id="integration.nudge_click" />
                      </h6>
                      <p className="text-muted text-small mb-0">
                        Track when user clicks nudge messages, eyecatchers, or nudge
                        buttons.
                      </p>
                    </div>
                    <Switch
                      checked={nudgesEnabled}
                      onChange={setNudgesEnabled}
                      disabled={sectionDisabled}
                    />
                  </div>
                  <div>
                    <Label className="mb-1">
                      <IntlMessages id="integration.event_tag" />
                    </Label>
                    <Input
                      type="text"
                      value={nudgesTag}
                      onChange={(e) => setNudgesTag(e.target.value)}
                      disabled={!nudgesEnabled || sectionDisabled}
                    />
                    <p className="text-muted text-small my-1">
                      <IntlMessages id="integration.final_tag" />:{' '}
                      <code>{sanitizeTag(nudgesTag)}</code>
                    </p>
                  </div>
                </CardBody>
              </Card>

              {/* CHAT STARTED */}
              <Card className="">
                <CardBody className="my-2">
                  <div className="mb-3 d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">
                        <IntlMessages id="integration.chat_started" />
                      </h6>
                      <p className="text-muted text-small mb-0">
                        <IntlMessages id="integration.chat_started_desc" />
                      </p>
                    </div>
                    <Switch
                      checked={chatStartedEnabled}
                      onChange={setChatStartedEnabled}
                      disabled={sectionDisabled}
                    />
                  </div>
                  <div>
                    <Label className="mb-1">
                      <IntlMessages id="integration.event_tag" />
                    </Label>
                    <Input
                      type="text"
                      value={chatStartedTag}
                      onChange={(e) => setChatStartedTag(e.target.value)}
                      disabled={!chatStartedEnabled || sectionDisabled}
                    />
                    <p className="text-muted text-small my-1">
                      <IntlMessages id="integration.final_tag" />:{' '}
                      <code>{sanitizeTag(chatStartedTag)}</code>{' '}
                      <span className="text-info">
                        (<IntlMessages id="integration.once_per_session" />)
                      </span>
                    </p>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* SUCCESS MESSAGE */}
            {successMsg && (
              <Alert color="success" className="mt-3">
                {successMsg}
              </Alert>
            )}

            {/* GENERAL ERROR */}
            {generalError && (
              <div
                className="mt-3 px-3 py-2"
                style={{
                  background: '#fff3cd',
                  border: '1px solid #ffeeba',
                  borderRadius: '8px',
                  color: '#856404',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {generalError}
              </div>
            )}
          </Card>
        </Card>
        <Separator className="mb-3" />

        {/* SAVE BUTTON */}
        <div className="text-center mb-1">
          <Button
            color="primary"
            size="xs"
            className="my-2"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {saving ? (
              <IntlMessages id="integration.saving" />
            ) : (
              <IntlMessages id="integration.save_ga4_settings" />
            )}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default HelpCenterGA4Tracking;
